import React, { useState } from 'react';
import { SEQUENTIAL, fmt, prettyLabel } from './vizTokens';

/**
 * Magnitude breakdown for one dimension (document type, source, department).
 *
 * Single hue, not a categorical palette: each card is one series answering
 * "how many of each", and colouring every bar differently would spend the
 * identity channel on information the bar length already carries. Rows with a
 * zero value are kept and dimmed — the API seeds every enum member precisely so
 * "none yet" stays visible instead of disappearing from the page.
 */
const DistributionCard = ({ title, icon: Icon, data, sortByCount = false, emptyHint }) => {
  const [active, setActive] = useState(null);
  const entries = Object.entries(data ?? {});
  if (!entries.length) return null;

  const rows = sortByCount ? [...entries].sort(([, a], [, b]) => b - a) : entries;
  const total = entries.reduce((sum, [, count]) => sum + count, 0);
  const max = Math.max(...entries.map(([, c]) => c), 1);

  return (
    <div className="card">
      <h3 className="text-base font-semibold text-slate-800 mb-1 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-slate-400" aria-hidden="true" />}
        {title}
      </h3>
      <p className="text-xs text-slate-400 mb-4">
        {total > 0 ? `${fmt(total)} documents` : emptyHint ?? 'No documents yet'}
      </p>

      <div className="space-y-2.5">
        {rows.map(([key, count]) => {
          const share = total > 0 ? (count / total) * 100 : 0;
          const isActive = active === key;
          return (
            <div
              key={key}
              tabIndex={0}
              onMouseEnter={() => setActive(key)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(key)}
              onBlur={() => setActive(null)}
              className="group grid grid-cols-[8rem_1fr_3.5rem] items-center gap-3 rounded-md -mx-1 px-1 py-1 focus:outline-none focus:ring-2 focus:ring-blue-200"
              title={`${prettyLabel(key)}: ${fmt(count)} (${share.toFixed(1)}%)`}
            >
              <span
                className={`text-sm truncate ${count === 0 ? 'text-slate-400' : 'text-slate-600'}`}
              >
                {prettyLabel(key)}
              </span>

              <div
                className="h-2 rounded-full overflow-hidden"
                style={{ backgroundColor: SEQUENTIAL.track }}
              >
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${(count / max) * 100}%`,
                    backgroundColor: SEQUENTIAL.base,
                    opacity: active === null || isActive ? 1 : 0.55,
                  }}
                />
              </div>

              {/* Value at the tip — the row is short enough that every value fits */}
              <span
                className={`text-sm text-right tabular-nums ${
                  count === 0 ? 'text-slate-300' : 'text-slate-700 font-medium'
                }`}
              >
                {isActive && total > 0 ? `${share.toFixed(0)}%` : fmt(count)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DistributionCard;
